import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Globe, LogIn } from "lucide-react";
import axios from "axios";

const LANGS = ["fr", "en", "es"];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Header = () => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) return;
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => setUser(r.data))
      .catch(() => setUser(null));
  }, []);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const links = [
    { href: "#product", label: t("nav.product"), id: "nav-link-product" },
    { href: "#pricing", label: t("nav.pricing"), id: "nav-link-pricing" },
    { href: "#security", label: t("nav.security"), id: "nav-link-security" },
    { href: "#faq", label: t("nav.faq"), id: "nav-link-faq" },
  ];

  return (
    <header data-testid="header-nav" className="fixed top-0 inset-x-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" data-testid="header-logo" className="font-display font-semibold text-xl tracking-tight text-white">
          Wadöo<span className="text-mint">.</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <a key={l.href} href={l.href} data-testid={l.id} className="text-sm text-white/60 hover:text-white transition-colors duration-200">
              {l.label}
            </a>
          ))}
          <Link to="/blog" data-testid="nav-link-blog" className="text-sm text-white/60 hover:text-white transition-colors duration-200">
            {t("nav.blog")}
          </Link>
          <Link to="/estimateur" data-testid="nav-link-estimate" className="text-sm text-white/60 hover:text-white transition-colors duration-200">
            {t("nav.estimate")}
          </Link>
        </nav>

        <div className="flex items-center gap-3">
          <div data-testid="language-switcher" className="flex items-center gap-1 rounded-full border border-white/10 bg-white/5 p-1">
            <Globe className="w-3.5 h-3.5 text-white/40 ml-1.5" strokeWidth={1.5} />
            {LANGS.map((lng) => (
              <button
                key={lng}
                data-testid={`lang-switcher-${lng}`}
                onClick={() => i18n.changeLanguage(lng)}
                className={`px-2 py-1 rounded-full text-xs font-medium uppercase transition-colors duration-200 ${
                  i18n.language === lng ? "bg-mint text-night" : "text-white/50 hover:text-white"
                }`}
              >
                {lng}
              </button>
            ))}
          </div>
          {user ? (
            <Link
              to="/account"
              data-testid="header-account-link"
              className="hidden sm:flex items-center gap-2 rounded-full border border-white/15 pl-1.5 pr-4 py-1.5 transition-colors duration-200 hover:border-mint/50"
            >
              {user.picture ? (
                <img src={user.picture} alt="" className="w-6 h-6 rounded-full" referrerPolicy="no-referrer" />
              ) : (
                <span className="w-6 h-6 rounded-full bg-mint/20 text-mint text-xs font-semibold flex items-center justify-center">
                  {user.name?.[0] || "W"}
                </span>
              )}
              <span className="text-sm text-white/80">{t("auth.myAccount")}</span>
            </Link>
          ) : (
            <button
              onClick={login}
              data-testid="header-login-button"
              className="hidden sm:inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-medium text-white/80 transition-colors duration-200 hover:border-mint/50 hover:text-white"
            >
              <LogIn className="w-4 h-4" strokeWidth={1.5} />
              {t("auth.login")}
            </button>
          )}
          <a
            href="#waitlist"
            data-testid="header-cta-button"
            className="hidden sm:inline-flex items-center rounded-full bg-mint px-4 py-2 text-sm font-semibold text-night transition-transform duration-200 hover:scale-105"
          >
            {t("nav.cta")}
          </a>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-white/70 hover:text-white"
            aria-label="Menu"
          >
            {open ? <X className="w-5 h-5" strokeWidth={1.5} /> : <Menu className="w-5 h-5" strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden overflow-hidden border-t border-white/10 bg-night/95"
          >
            <div className="px-6 py-4 flex flex-col gap-4">
              {links.map((l) => (
                <a key={l.href} href={l.href} data-testid={`mobile-${l.id}`} onClick={() => setOpen(false)} className="text-sm text-white/70">
                  {l.label}
                </a>
              ))}
              <Link to="/blog" data-testid="mobile-nav-link-blog" onClick={() => setOpen(false)} className="text-sm text-white/70">
                {t("nav.blog")}
              </Link>
              <Link to="/estimateur" data-testid="mobile-nav-link-estimate" onClick={() => setOpen(false)} className="text-sm text-white/70">
                {t("nav.estimate")}
              </Link>
              {user ? (
                <Link to="/account" data-testid="mobile-nav-account" onClick={() => setOpen(false)} className="text-sm text-mint">
                  {t("auth.myAccount")}
                </Link>
              ) : (
                <button
                  data-testid="mobile-nav-login"
                  onClick={() => { setOpen(false); login(); }}
                  className="text-left text-sm text-white/70"
                >
                  {t("auth.login")}
                </button>
              )}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
